import { useMemo, useState, useCallback } from "react";
import type { MapLayerMouseEvent, LngLatBounds } from "maplibre-gl";
import type { Sample, Core } from "@rogoag/shared-ts";
import type { OpsMapProps, OpsPopup, VisibleLayers, MissionPointView } from "./OpsMap.types";
import { buildJobFCs, buildMissionFCs, buildRobotFCs, computeBounds } from "./OpsMap.helpers";
import { TILE_SOURCES } from "../JobMap/tileSources";

export function useOpsMapViewModel(props: OpsMapProps) {
	const jobs = props.jobs ?? [];
	const missions = props.missions ?? [];
	const robots = props.robots ?? [];

	const [popup, setPopup] = useState<OpsPopup | null>(null);
	const [fullscreen, setFullscreen] = useState(false);
	const GOOGLE_SOURCES = TILE_SOURCES.filter((t) => t.id.startsWith("google-"));
	const [tileSourceIndex, setTileSourceIndex] = useState(0);
	const tileSource = GOOGLE_SOURCES[tileSourceIndex] ?? GOOGLE_SOURCES[0];
	const [visibleLayers, setVisibleLayers] = useState<VisibleLayers>({
		jobs: true,
		missions: true,
		robots: true,
	});
	const [missionPointView, setMissionPointView] = useState<MissionPointView>("samples");

	// ── Build feature collections ──────────────────────────────────────────
	const jobData = useMemo(() => buildJobFCs(jobs), [jobs]);
	const missionData = useMemo(() => buildMissionFCs(missions), [missions]);
	const robotData = useMemo(() => buildRobotFCs(robots), [robots]);

	// ── Active mission point FC based on view toggle ──────────────────────
	const missionPointFC = useMemo(() => {
		if (missionPointView === "samples") return missionData.sampleFC;
		if (missionPointView === "cores") return missionData.coreFC;
		return missionData.allPointFC;
	}, [missionData, missionPointView]);

	const hasMissionSamples = missionData.sampleFC.features.length > 0;
	const hasMissionCores = missionData.coreFC.features.length > 0;
	const showMissionPointToggle = hasMissionSamples && hasMissionCores;

	// ── Bounds ────────────────────────────────────────────────────────────
	const { bounds, missionBounds } = useMemo(
		() => computeBounds(jobData.bounds, missionData, robotData),
		[jobData.bounds, missionData, robotData],
	);
	// Prefer mission bounds for initial zoom; fall back to combined
	const initialBounds = missionBounds ?? bounds;

	// ── Has any data ───────────────────────────────────────────────────────
	const hasAnyData =
		jobData.polygonFC.features.length > 0 ||
		jobData.pointFC.features.length > 0 ||
		missionData.polygonFC.features.length > 0 ||
		missionData.allPointFC.features.length > 0 ||
		missionData.lineFC.features.length > 0 ||
		robotData.pointFC.features.length > 0 ||
		robotData.trailFC.features.length > 0;

	// ── Interactive layer IDs for click detection ──────────────────────────
	const interactiveLayerIds = useMemo(() => {
		const ids: string[] = [];
		if (visibleLayers.jobs) ids.push("ops-job-polygon-fill");
		if (visibleLayers.missions) ids.push("ops-mission-points");
		if (visibleLayers.robots) ids.push("ops-robot-points");
		return ids;
	}, [visibleLayers]);

	// ── Click handler ──────────────────────────────────────────────────────
	const handleClick = useCallback(
		(e: MapLayerMouseEvent) => {
			const feature = e.features?.[0];
			if (!feature) {
				setPopup(null);
				return;
			}

			const layerId = feature.layer?.id ?? "";
			const lng = e.lngLat.lng;
			const lat = e.lngLat.lat;

			// Job polygon click
			if (layerId.startsWith("ops-job-")) {
				const jobId = feature.properties?.jobId as string;
				const job = jobData.jobLookup.get(jobId);
				if (job) {
					setPopup({ type: "job", lng, lat, job });
				}
				return;
			}

			// Mission point click
			if (layerId.startsWith("ops-mission-")) {
				const missionIndex = feature.properties?.missionIndex as number;
				const mission = missions[missionIndex];
				if (!mission) return;

				const rogoId = feature.properties?.rogo_id as string | undefined;
				const isCore = !!feature.properties?.core_location;

				// Try to find by rogo_id first
				if (rogoId) {
					if (isCore) {
						const core = mission.cores.find((c) => c.id === rogoId);
						if (core) {
							setPopup({ type: "core", lng, lat, core });
							return;
						}
					} else {
						const sample = mission.centroids.find((s) => s.id === rogoId);
						if (sample) {
							setPopup({ type: "sample", lng, lat, sample });
							return;
						}
					}
				}

				// Fallback: find closest point
				const searchSamples = missionPointView !== "cores";
				const searchCores = missionPointView !== "samples";
				const candidates: (Sample | Core)[] = [
					...(searchSamples ? mission.centroids : []),
					...(searchCores ? mission.cores : []),
				];
				let closest: (Sample | Core) | null = null;
				let minDist = Infinity;
				for (const pt of candidates) {
					const dx = pt.longitude - lng;
					const dy = pt.latitude - lat;
					const dist = dx * dx + dy * dy;
					if (dist < minDist) {
						minDist = dist;
						closest = pt;
					}
				}
				if (closest) {
					const isSample = "cores" in closest;
					setPopup({
						type: isSample ? "sample" : "core",
						lng,
						lat,
						...(isSample ? { sample: closest as Sample } : { core: closest as Core }),
					} as OpsPopup);
				}
				return;
			}

			// Robot point click
			if (layerId.startsWith("ops-robot-")) {
				const robotId = feature.properties?.id as string;
				const robot = robots.find((r) => r.id === robotId);
				if (robot) {
					setPopup({ type: "robot", lng, lat, robot });
				}
				return;
			}

			setPopup(null);
		},
		[jobData.jobLookup, missions, robots, missionPointView],
	);

	// ── Layer toggle ───────────────────────────────────────────────────────
	const toggleLayer = useCallback((layer: keyof VisibleLayers) => {
		setVisibleLayers((prev) => ({ ...prev, [layer]: !prev[layer] }));
	}, []);

	// ── Tile source toggle ────────────────────────────────────────────────
	const toggleTileSource = useCallback(() => {
		setTileSourceIndex((prev) => (prev + 1) % GOOGLE_SOURCES.length);
	}, [GOOGLE_SOURCES.length]);

	// ── Fit bounds helper ──────────────────────────────────────────────────
	const fitBoundsTo = useCallback((mapRef: React.RefObject<any>, target: LngLatBounds | undefined) => {
		const map = mapRef.current;
		if (!map || !target) return;
		map.fitBounds(
			[
				[target.getWest(), target.getSouth()],
				[target.getEast(), target.getNorth()],
			],
			{ padding: 60, maxZoom: 14 },
		);
	}, []);
	const fitBounds = useCallback(
		(mapRef: React.RefObject<any>) => fitBoundsTo(mapRef, initialBounds),
		[fitBoundsTo, initialBounds],
	);

	return {
		jobData,
		missionData,
		robotData,
		missionPointFC,
		bounds,
		popup,
		setPopup,
		fullscreen,
		setFullscreen,
		visibleLayers,
		toggleLayer,
		missionPointView,
		setMissionPointView,
		showMissionPointToggle,
		hasAnyData,
		interactiveLayerIds,
		handleClick,
		tileSource,
		toggleTileSource,
		fitBounds,
	};
}
